import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

interface HermesLogoProps {
  open: boolean;
}

/**
 * Animated Hermes logo using actual logo512.png
 * Collapses/expands with drawer
 */
export const HermesLogo: React.FC<HermesLogoProps> = ({ open }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: open ? "flex-start" : "center",
        padding: open ? theme.spacing(2) : theme.spacing(1.5, 0.5),
        minHeight: 64,
        overflow: "hidden",
      }}
    >
      {/* Hermes Logo Image */}
      <motion.img
        src={`${process.env.PUBLIC_URL}/logo512.png`}
        alt="Hermes Logo"
        style={{
          width: open ? 48 : 40,
          height: open ? 48 : 40,
          objectFit: "contain",
        }}
        animate={{
          scale: open ? 1 : 0.95,
        }}
        transition={{
          type: "spring",
          stiffness: 200,
          damping: 20,
        }}
      />

      {/* Animated Text - with AnimatePresence for smooth exit */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="logo-text"
            initial={{ opacity: 0, x: -20, width: 0 }}
            animate={{
              opacity: 1,
              x: 0,
              width: "auto",
            }}
            exit={{
              opacity: 0,
              x: -20,
              width: 0,
            }}
            transition={{
              duration: 0.25,
              ease: "easeOut",
            }}
            style={{
              overflow: "hidden",
              marginLeft: theme.spacing(1.5),
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Instrument Sans", sans-serif',
                fontWeight: 700,
                color: theme.palette.primary.main,
                whiteSpace: "nowrap",
                letterSpacing: "-0.02em",
              }}
            >
              Hermes
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: '"Instrument Serif", serif',
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
                display: "block",
                marginTop: "-4px",
                fontStyle: "italic",
              }}
            >
              Connecting Service
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
